
import React, { useEffect, useState } from 'react';
import { statsService } from '../lib/stats';
import { ICONS, COLOR_THEMES, FONTS } from '../constants';
import type { IconType } from '../types';

interface StatsModalProps {
    onClose: () => void;
    allIcons?: IconType[];
}

const StatsModal: React.FC<StatsModalProps> = ({ onClose, allIcons = [] }) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const stats = await statsService.getStats();
            if (stats) {
                setData(stats);
            } else {
                setError("Nepodařilo se načíst data.");
            }
        } catch (e) {
            setError("Chyba při načítání dat.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        
        // Safety timeout
        const timeout = setTimeout(() => {
            if (loading) {
                setLoading(false);
                if (!data) setError("Timeout: Databáze neodpovídá.");
            }
        }, 5000);

        return () => clearTimeout(timeout);
    }, []);

    const isDataEmpty = !data || (
        Object.keys(data.icons || {}).length === 0 && 
        Object.keys(data.themes || {}).length === 0
    );

    const handleRetry = () => {
        load();
    };

    const handleGenerateTestData = async () => {
        setLoading(true);
        try {
            await statsService.logTestStat();
            setTimeout(load, 1000); // Wait a bit then reload
        } catch (e: any) {
            setError(`Chyba zápisu: ${e.message}`);
            setLoading(false);
        }
    };

    // --- CSV EXPORT LOGIC ---
    const downloadCSV = () => {
        console.log("Starting CSV export...");
        if (!data) {
            console.error("No data to export");
            return;
        }

        try {
            let csvContent = "\uFEFF"; // UTF-8 BOM
            csvContent += "Kategorie;ID;Nazev;Pocet\n";

            // Helper to append rows
            const appendRows = (category: string, sourceData: Record<string, number>, lookupList?: any[], nameKey='name', idKey='id') => {
                Object.entries(sourceData).forEach(([key, count]) => {
                    let name = key;
                    if (lookupList) {
                        const found = lookupList.find(i => i[idKey] === key);
                        if (found) name = found[nameKey];
                    } else {
                        // Variants names
                        if(key === 'square-sm') name = 'Čtverec malý';
                        if(key === 'circle-sm') name = 'Kruh malý';
                        if(key === 'rectangle-sm') name = 'Obdélník malý';
                        if(key === 'rectangle-md') name = 'Obdélník střední';
                        if(key === 'rectangle-lg') name = 'Obdélník velký';
                        if(key === 'circle-lg') name = 'Kruh velký';
                        if(key.startsWith('shape-')) name = 'Vlastní tvar';
                    }
                    // Escape semicolons in names
                    name = name.replace(/;/g, ',');
                    csvContent += `${category};${key};${name};${count}\n`;
                });
            };

            appendRows('Typy', data.variants || {});
            appendRows('Barvy', data.themes || {}, COLOR_THEMES);
            
            // Merge default ICONS with passed allIcons (custom) for lookup
            const mergedIcons = [...ICONS, ...allIcons]; 
            const uniqueIconsMap = new Map();
            mergedIcons.forEach(i => uniqueIconsMap.set(i.id, i));
            const uniqueIcons = Array.from(uniqueIconsMap.values());

            appendRows('Ikony', data.icons || {}, uniqueIcons);
            appendRows('Písma', data.fonts || {}, FONTS);

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `statistiky_samolepky_${new Date().toISOString().slice(0,10)}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            console.log("CSV download triggered");
        } catch (err) {
            console.error("CSV Export failed:", err);
            alert("Chyba při exportu CSV. Zkontrolujte konzoli.");
        }
    };

    const renderTableSection = (title: string, dataObj: Record<string, number>, lookupList?: any[], nameKey: string = 'name', idKey: string = 'id') => {
        if (!dataObj) return null;
        
        const totalUsage = Object.values(dataObj).reduce((a, b) => a + b, 0);
        
        const sorted = Object.entries(dataObj)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 20); // Top 20 rows

        return (
            <div className="bg-white rounded-lg border shadow-sm overflow-hidden flex flex-col h-full mb-4">
                <div className="bg-gray-50 px-4 py-3 border-b flex justify-between items-center">
                    <h3 className="text-sm font-bold text-gray-800 uppercase">{title}</h3>
                    <span className="text-xs text-gray-500">Celkem záznamů: {totalUsage}</span>
                </div>
                
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="text-xs text-gray-500 bg-white border-b uppercase">
                            <tr>
                                <th className="px-4 py-2 w-10 text-center">#</th>
                                <th className="px-4 py-2">Název</th>
                                <th className="px-4 py-2 text-right">Počet</th>
                                <th className="px-4 py-2 w-1/4">Podíl</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {sorted.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="px-4 py-8 text-center text-gray-400 italic">Žádná data k zobrazení</td>
                                </tr>
                            ) : (
                                sorted.map(([key, val], index) => {
                                    let label = key;
                                    
                                    // Lookup logic
                                    if (lookupList) {
                                        const item = lookupList.find(i => i[idKey] === key);
                                        if (item) {
                                            label = item[nameKey];
                                        } else if (key.startsWith('custom-')) {
                                            label = `Vlastní ikona (${key.substr(0,8)}...)`;
                                        }
                                    }
                                    
                                    // Custom naming for variants
                                    if (title.includes('typy') && !lookupList) {
                                         if(key === 'square-sm') label = 'Čtverec malý';
                                         if(key === 'circle-sm') label = 'Kruh malý';
                                         if(key === 'rectangle-sm') label = 'Obdélník malý';
                                         if(key === 'rectangle-md') label = 'Obdélník střední';
                                         if(key === 'rectangle-lg') label = 'Obdélník velký';
                                         if(key === 'circle-lg') label = 'Kruh velký';
                                         if(key.startsWith('shape-')) label = 'Vlastní tvar';
                                    }

                                    const percent = totalUsage > 0 ? (val / totalUsage) * 100 : 0;

                                    return (
                                        <tr key={key} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-4 py-2 text-center text-gray-400 font-mono text-xs">{index + 1}.</td>
                                            <td className="px-4 py-2 font-medium text-gray-800 truncate max-w-[200px]" title={label}>{label}</td>
                                            <td className="px-4 py-2 text-right font-bold text-gray-700">{val}</td>
                                            <td className="px-4 py-2">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                                                        <div className="h-full bg-blue-600 rounded-full" style={{ width: `${percent}%` }}></div>
                                                    </div>
                                                    <span className="text-[10px] text-gray-400 w-8 text-right">{Math.round(percent)}%</span>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // Merge default icons with custom ones for the lookup
    const lookupIcons = [...ICONS, ...allIcons];
    const uniqueIconsMap = new Map();
    lookupIcons.forEach(i => uniqueIconsMap.set(i.id, i));
    const uniqueIcons = Array.from(uniqueIconsMap.values());

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-100 rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 bg-white border-b border-gray-200 flex justify-between items-center rounded-t-xl shrink-0">
                    <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        Statistiky používání
                    </h2>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto bg-gray-100 flex-1 relative">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-full min-h-[300px]">
                            <svg className="animate-spin h-10 w-10 text-purple-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                            <span className="text-gray-500 font-medium">Načítám data z databáze...</span>
                        </div>
                    ) : isDataEmpty ? (
                        <div className="flex flex-col items-center justify-center h-full min-h-[300px] text-center">
                            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 max-w-md">
                                <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                <h3 className="text-xl font-bold text-gray-800 mb-2">Databáze je prázdná</h3>
                                <p className="text-gray-500 mb-6">Zatím nebyly zaznamenány žádné objednávky. Pokud aplikaci teprve nastavujete, můžete vygenerovat testovací data.</p>
                                <button 
                                    onClick={handleGenerateTestData}
                                    className="px-6 py-3 bg-blue-600 text-white font-bold rounded-lg shadow hover:bg-blue-700 transition-colors"
                                >
                                    Vygenerovat testovací data
                                </button>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="text-center py-20 text-red-600 bg-white rounded-lg shadow">
                            <p className="text-lg font-bold mb-2">{error}</p>
                            <button onClick={handleRetry} className="mt-4 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded border">Zkusit znovu</button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-6">
                            {renderTableSection('Nejoblíbenější typy samolepek', data?.variants)}
                            {renderTableSection('Nejoblíbenější barvy', data?.themes, COLOR_THEMES)}
                            {renderTableSection('Nejoblíbenější ikony', data?.icons, uniqueIcons)}
                            {renderTableSection('Nejoblíbenější písma', data?.fonts, FONTS)}
                        </div>
                    )}
                </div>
                
                {/* Footer with CSV Button */}
                <div className="px-6 py-4 bg-white border-t border-gray-200 flex justify-between items-center shrink-0">
                    <span className="text-xs text-gray-400">Data jsou aktualizována v reálném čase.</span>
                    
                    <button 
                        onClick={downloadCSV} 
                        disabled={isDataEmpty || loading}
                        className="flex items-center px-6 py-3 bg-green-600 text-white font-bold rounded-lg shadow hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        Exportovat statistiky (CSV)
                    </button>
                </div>
            </div>
        </div>
    );
};

export default StatsModal;
